import axios from "./axios"; // Use configured axios instance (auto-adds auth + ip headers)
import BASEURL from "../Config/global";


const translateViaBackend = async ({ text, target, source = null }) => {
  if (!text || (Array.isArray(text) && text.length === 0)) {
    return {
      success: false,
      error: "No text provided for translation",
      originalText: text,
    };
  }

  try {
    const payload = {
      text,
      target,
      ...(source ? { source } : {}),
    };
    let URL = `${BASEURL}/api/misc/translate/`;
    if (target === 'en') {
      URL = `${BASEURL}/api/misc/spanish-to-english/`;
    }
    const response = await axios.post(URL, payload);
    // Backend returns: { translated_text: string[] }
    if (response.data && Array.isArray(response.data.translated_text)) {
      const translatedTexts = response.data.translated_text;

      // Normalize to array of objects for consistent handling
      const normalizedTranslations = translatedTexts.map((translatedText) => ({
        translatedText,
      }));

      return {
        success: true,
        translations: normalizedTranslations,
        originalText: text,
      };
    }

    return {
      success: false,
      error: "Invalid response format from translation backend",
      originalText: text,
    };
  } catch (error) {
    console.error("Translation Backend Error:", error);
    return {
      success: false,
      error:
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Translation failed",
      originalText: text,
    };
  }
};


export const translateToSpanish = async (text, sourceLanguage = null) => {
  const result = await translateViaBackend({
    text,
    target: "es",
    source: sourceLanguage,
  });

  if (!result.success || !result.translations?.length) return result;

  return {
    success: true,
    translatedText: result.translations[0].translatedText,
    detectedSourceLanguage: result.translations[0].detectedSourceLanguage,
    originalText: text,
  };
};


export const translateFromSpanish = async (text) => {
  const result = await translateViaBackend({
    text,
    target: "en",
    source: "es",
  });

  if (!result.success || !result.translations?.length) return result;

  return {
    success: true,
    translatedText: result.translations[0].translatedText,
    detectedSourceLanguage: "es",
    originalText: text,
  };
};


export const translateEnglishToSpanish = async (text) => {
  return await translateToSpanish(text, "en");
};


export const translateEnglishToSpanishBatch = async (texts = []) => {
  if (!Array.isArray(texts) || texts.length === 0) {
    return { success: false, error: "No texts provided for translation" };
  }

  const result = await translateViaBackend({
    text: texts,
    target: "es",
    source: "en",
  });

  if (!result.success || !Array.isArray(result.translations)) {
    return {
      success: false,
      error: result.error || "Batch translation failed",
    };
  }

  const translatedTexts = result.translations.map((t) => t.translatedText);

  return {
    success: true,
    translatedTexts,
  };
};


export const translateSpanishToEnglishBatch = async (texts = []) => {
  if (!Array.isArray(texts) || texts.length === 0) {
    return { success: false, error: "No texts provided for translation" };
  }

  const result = await translateViaBackend({
    text: texts,
    target: "en",
    source: "es",
  });

  if (!result.success || !Array.isArray(result.translations)) {
    return {
      success: false,
      error: result.error || "Batch translation failed",
    };
  }

  const translatedTexts = result.translations.map((t) => t.translatedText);

  return {
    success: true,
    translatedTexts,
  };
};

/**
 * Smart translation utility for mix tracks based on user language and track languages
 * 
 * Translation rules:
 * - Spanish UI + English tracks → translate to Spanish
 * - English UI + Spanish tracks → translate to English  
 * - Same language (Spanish UI + Spanish tracks OR English UI + English tracks) → no translation
 * 
 * @param {Array} tracks - Array of track objects with title/name and language properties
 * @param {string} userLanguage - Current user UI language ('es' or 'en')
 * @param {Object} options - Optional configuration
 * @param {Function} options.getTitle - Function to extract title from track (default: d => d?.title || d?.name)
 * @param {Function} options.getLanguage - Function to extract language from track (default: d => d?.language)
 * @returns {Promise<Array>} - Translated tracks array (or original if no translation needed)
 */
export const translateTracksBasedOnLanguage = async (tracks = [], userLanguage, options = {}) => {
  if (!Array.isArray(tracks) || tracks.length === 0) {
    return tracks;
  }

  const {
    getTitle = (d) => d?.title || d?.name || "",
    getLanguage = (d) => d?.language,
  } = options;

  try {
    const trackLanguages = tracks.map(getLanguage);
    
    // Spanish tracks: language === "spanish" || language === "es"
    const hasSpanishTracks = trackLanguages.some(
      (lang) => {
        const normalizedLang = lang?.toLowerCase();
        return normalizedLang === "spanish" || normalizedLang === "es";
      }
    );
    
    // English tracks: language === "english" || language === "en" || language is undefined/null
    const hasEnglishTracks = trackLanguages.some(
      (lang) => {
        if (lang === undefined || lang === null || lang === "") {
          return true; // No language property = English (default)
        }
        const normalizedLang = lang?.toLowerCase();
        return normalizedLang === "english" || normalizedLang === "en";
      }
    );

    // Normalize user language
    const normalizedUserLang = userLanguage?.toLowerCase() === "es" ? "es" : "en";

    // Determine if translation is needed
    let needsTranslation = false;
    let translationDirection = null; // 'en-to-es' or 'es-to-en'

    if (normalizedUserLang === "es" && hasEnglishTracks && !hasSpanishTracks) {
      // Spanish UI + English tracks → translate to Spanish
      needsTranslation = true;
      translationDirection = "en-to-es";
    } else if (normalizedUserLang === "en" && hasSpanishTracks && !hasEnglishTracks) {
      // English UI + Spanish tracks → translate to English
      needsTranslation = true;
      translationDirection = "es-to-en";
    }
    // Otherwise: same language or mixed → no translation

    if (!needsTranslation) {
      return tracks;
    }

    // Extract texts to translate
    const texts = tracks.map(getTitle);

    // Perform batch translation
    let translationResult;
    if (translationDirection === "en-to-es") {
      translationResult = await translateEnglishToSpanishBatch(texts);
    } else if (translationDirection === "es-to-en") {
      translationResult = await translateSpanishToEnglishBatch(texts);
    }

    if (
      translationResult?.success &&
      Array.isArray(translationResult.translatedTexts)
    ) {
      // Map translated texts back to tracks
      return tracks.map((track, idx) => ({
        ...track,
        title: translationResult.translatedTexts[idx] || track.title,
        name: translationResult.translatedTexts[idx] || track.name,
      }));
    }

    // Return original tracks if translation failed
    return tracks;
  } catch (error) {
    console.error("Failed to translate tracks:", error);
    return tracks; // Return original on error
  }
};


